import { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, FileText, File } from "lucide-react";
import { API, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ExportPage() {
  const { hasPerm } = useAuth();
  const [categories, setCategories] = useState([]);
  const [type, setType] = useState("");
  const [book, setBook] = useState("resmi");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const exportUrl = (fmt) => {
    const params = new URLSearchParams({ format: fmt, type, category, date_from: dateFrom, date_to: dateTo, book });
    return `${API}/export?${params.toString()}`;
  };

  const preset = (kind) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    if (kind === "month") {
      const last = new Date(y, now.getMonth() + 1, 0).getDate();
      setDateFrom(`${y}-${m}-01`);
      setDateTo(`${y}-${m}-${String(last).padStart(2, "0")}`);
    } else if (kind === "year") {
      setDateFrom(`${y}-01-01`);
      setDateTo(`${y}-12-31`);
    } else {
      setDateFrom("");
      setDateTo("");
    }
  };

  const formats = [
    { key: "xlsx", label: "Excel (.xlsx)", desc: "Laporan terformat dengan ringkasan saldo", icon: FileSpreadsheet, cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-400", testid: "btn-export-excel" },
    { key: "csv", label: "CSV (.csv)", desc: "Data mentah untuk diolah di spreadsheet", icon: File, cls: "border-slate-800 bg-[#0F172A] text-slate-200", testid: "btn-export-csv" },
    { key: "pdf", label: "PDF (.pdf)", desc: "Dokumen laporan siap cetak, landscape A4", icon: FileText, cls: "border-rose-500/30 bg-rose-500/15 text-rose-400", testid: "btn-export-pdf" },
  ];

  return (
    <div className="space-y-5 max-w-3xl" data-testid="export-page">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-100 font-semibold text-sm">
          <FileDown className="w-4 h-4" /> Opsi Export
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ["all", "Semua Periode"],
            ["month", "Bulan Ini"],
            ["year", "Tahun Ini"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`btn-preset-${k}`}
              onClick={() => preset(k)}
              className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-3 py-1.5 rounded-lg text-xs transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-400 mb-1">Kas</p>
            <select data-testid="select-export-book" className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={book} onChange={(e) => setBook(e.target.value)}>
              {hasPerm("view_saldo_resmi") && <option value="resmi">Kas Resmi</option>}
              {hasPerm("view_saldo_imf") && <option value="imf">Kas IMF</option>}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Jenis Transaksi</p>
            <select data-testid="select-export-type" className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Semua Transaksi</option>
              <option value="income">Pemasukan Saja</option>
              <option value="expense">Pengeluaran Saja</option>
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Kategori</p>
            <select data-testid="select-export-category" className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Semua Kategori</option>
              {[...new Set(categories.map((c) => c.name))].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Dari Tanggal</p>
            <input data-testid="input-export-date-from" type="date" className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Sampai Tanggal</p>
            <input data-testid="input-export-date-to" type="date" className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {formats.map((f) => (
          <a
            key={f.key}
            data-testid={f.testid}
            href={exportUrl(f.key)}
            className={`border rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col gap-3 ${f.cls}`}
          >
            <f.icon className="w-8 h-8" />
            <div>
              <p className="font-bold text-sm">{f.label}</p>
              <p className="text-xs opacity-80 mt-1">{f.desc}</p>
            </div>
            <span className="text-xs font-semibold flex items-center gap-1">
              <FileDown className="w-3.5 h-3.5" /> Unduh Sekarang
            </span>
          </a>
        ))}
      </div>
      <p className="text-xs text-slate-400">
        Laporan memuat header organisasi (dari Pengaturan), periode, seluruh transaksi terpilih dengan
        saldo berjalan, serta ringkasan saldo awal, pemasukan, pengeluaran, dan saldo akhir.
      </p>
    </div>
  );
}
